<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { isMediumDeviceAndUp } from "common/utils/browser";
import CommonModal from "common/vue/components/CommonModal.vue";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import { n } from "components/jiffy/vue-cart/helpers/currency";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { useDtfStore } from "../../store";
import type { Standard } from "../../types";
import Section from "../components/Section.vue";
import RedrawNotes from "../../RedrawNotes.vue";
import SmartRedrawNotes from "../../SmartRedrawNotes.vue";
import RedrawInformation from "../../Modals/RedrawInformation.vue";
import DesignServicesSubscribe from "./DesignServicesSubscribe.vue";

const props = defineProps<{
  artwork: Standard.Artwork;
  paidExpertEditAddonEnabled?: boolean;
  index: number;
  isMultiUpload?: boolean;
}>();

const store = useDtfStore();
const { n: numberFormatter } = useI18n();

const isStandardDTFProduct = computed(() => store.isStandardDTFProduct);

const isExpertEditAddonChecked = ref(props.artwork.hasExpertEditAddon || false);

const showRedrawUI = computed(() => {
  if (isStandardDTFProduct.value) {
    return true;
  }
  return isExpertEditAddonChecked.value;
});

const showSmartRedrawNotes = computed(() => (store.experiments as any).ab_smart_redraw_notes === 1);

const isLoading = computed(() => {
  const price = props.artwork.pricing?.expertEditAddonTierPrice;
  return price === undefined || price === null;
});
const isFree = computed(
  () => !isLoading.value && props.artwork.pricing.expertEditAddonTierPrice <= 0
);
const expertEditAddonTierPrice = computed(() => {
  if (isLoading.value) {
    return "";
  }
  if (isFree.value) {
    return "FREE";
  }
  return n(props.artwork.pricing.expertEditAddonTierPrice, "currency", numberFormatter);
});

watch(
  () => props.artwork.hasExpertEditAddon,
  (newValue: boolean | undefined) => {
    isExpertEditAddonChecked.value = newValue || false;
  }
);

// Modal state and controls
const isRedrawModalOpen = ref(false);
const isMobile = ref(!isMediumDeviceAndUp());
const redrawModal = ref<any>(null);

function openRedrawModal() {
  isRedrawModalOpen.value = true;
}

function closeRedrawModal() {
  if (redrawModal.value) {
    redrawModal.value.close();
  }
  isRedrawModalOpen.value = false;
}

function onExpertEditAddonToggle(checked: boolean) {
  if (props.artwork.isSimulated) {
    store.setSimulation({ hasExpertEditAddon: checked });
  } else {
    store.updateArtworkValuesById({
      id: props.artwork.id,
      hasExpertEditAddon: checked,
      enhancementNote: ""
    });
  }

  store.getArtworksPricing();

  if (store.showDesignServicesDeliveryBuffer) {
    // Trigger delivery estimates if:
    // - Unchecking (to reset with false flag)
    // - Checking AND notes have content (to enable with true flag)
    if (!checked || (checked && props.artwork.enhancementNote?.trim())) {
      store.updatePdpDates();
    }
  }

  submitDtfEvent("expert_edit_addon_toggled", {
    artwork_id: props.artwork.id,
    product_id: store.productId,
    enabled: checked,
    business_line: store.businessLine,
    catalog_number: store.catalogNumber,
    active_config_section: "full_enhancements"
  });
}

function handleCheckboxChange() {
  const checked = isExpertEditAddonChecked.value;
  onExpertEditAddonToggle(checked);
}

</script>

<template>
  <Section class="design-services">
    <div class="design-services__header">
      <label
        v-if="!isStandardDTFProduct"
        id="jiffy-design-services-addon-label"
        class="design-services__checkbox"
      >
        <input
          v-model="isExpertEditAddonChecked"
          class="design-services__input sr-only"
          type="checkbox"
          data-testid="print-perfect-design-services-checkbox"
          @change="handleCheckboxChange"
        />
        <div class="design-services__checkbox-box">
          <UseSvg
            v-if="isExpertEditAddonChecked"
            class="design-services__checkbox-icon"
            icon="check"
          />
        </div>
        <div class="design-services__label">
          <div class="design-services__label-text">
            Jiffy Design Services
            <span class="design-services__badge">Add-On</span>
          </div>
          <div class="design-services__help">Use our professional designers to edit your image</div>
        </div>
      </label>
      <div v-if="!isStandardDTFProduct" class="design-services__actions">
        <div class="design-services__price" :class="{ 'design-services__price--free': isFree }">
          {{ expertEditAddonTierPrice }}
        </div>
        <button class="design-services__learn-more" type="button" @click="openRedrawModal">
          Learn More
        </button>
      </div>
    </div>

    <div
      v-if="showRedrawUI"
      class="design-services__notes js-print-perfect-redraw-notes-addon"
      :data-id="artwork.id"
    >
      <SmartRedrawNotes v-if="showSmartRedrawNotes" :index="index" :artwork="artwork" />
      <RedrawNotes
        v-else
        :index="index"
        :enhancement-note="artwork.enhancementNote"
        v-bind="isStandardDTFProduct ? { onLearnMore: openRedrawModal } : {}"
      />
    </div>
    <DesignServicesSubscribe v-if="!isMultiUpload" />
  </Section>

  <!-- Expert Edit Learn More Modal -->
  <CommonModal
    v-if="isRedrawModalOpen"
    ref="redrawModal"
    :compact="true"
    :hide-close-button="false"
    :bottom-aligned="isMobile"
    @on-close="closeRedrawModal"
  >
    <RedrawInformation :is-mobile="isMobile" @close="closeRedrawModal" />
  </CommonModal>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings";
@import "../ArtworkQualityConfigPanel.scss";

.design-services {
  margin-top: 16px;
  padding: 16px;
  background-color: $white;
  border: 1px solid $crafterpay-gray-modern-400;
  border-radius: 8px;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: 16px;
    min-width: 0;
  }

  &__checkbox {
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
    margin-right: 12px;
  }

  &__input {
    &:checked + .design-services__checkbox-box {
      background-color: $primary-light;
      border-color: $primary-light;
    }
  }

  &__checkbox-box {
    position: relative;
    display: inline-block;
    width: 24px;
    height: 24px;
    border: 2px solid $crafterpay-gray-modern-400;
    border-radius: 4px;
    background-color: $white;
    margin-right: 12px;
    flex-shrink: 0;
  }

  &__checkbox-icon {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 18px;
    height: 18px;
    color: $white;
    pointer-events: none;
  }

  &__label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1 1 auto;
    min-width: 0;
  }

  &__label-text {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 700;
    line-height: 18px;
  }

  &__badge {
    background-color: $primary-light;
    color: $white;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.3;
    flex-shrink: 0;
    white-space: nowrap;
  }

  &__help {
    font-size: 12px;
    font-weight: 400;
    color: $crafterpay-gray-modern-600;
    line-height: 1.4;

    @include with-quality-container {
      font-size: 14px;
    }
  }

  &__actions {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    flex: 0 0 auto;
  }

  &__price {
    color: $price-product-card;
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    white-space: nowrap;

    &--free {
      color: $green-discount-text;
    }

    @include with-quality-container {
      font-size: 22px;
    }
  }

  &__learn-more {
    background: none;
    border: none;
    color: $blue;
    text-decoration: underline;
    cursor: pointer;
    font-size: 14px;
    padding: 0;
    line-height: 1;
    transition: color 0.2s ease;

    &:hover {
      color: darken($blue, 10%);
    }

    &:focus-visible {
      outline: 2px solid $blue;
      outline-offset: 2px;
      border-radius: 2px;
    }
  }

  &__notes {
    width: 100%;
  }

  &__subscribe-label {
    font-size: 14px;
    margin-bottom: 4px;
  }

  &__subscribe {
    margin-top: 16px;
  }

  &__subscribe-fields {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__subscribe-input {
    flex: 1 1 auto;
    padding: 11px;
    border: 1px solid $crafterpay-gray-modern-400;
    border-radius: 8px;
    font-size: 14px;
  }

  &__subscribe-button {
    flex: 0 0 auto;
    padding: 8px 16px;
    background-color: transparent;
    color: #414192;
    border: 2px solid #414192;
    border-radius: 8px;
    font-size: 14px;
    cursor: pointer;

    &--submitted {
      background-color: #00a65f;
      color: $white;
      border: none;
    }
  }

  &__subscribe-button-text {
    position: relative;
    display: inline-block;
  }

  &__subscribe-button-asterisk {
    position: absolute;
    top: -3px;
    right: -8px;
    font-size: 20px;
  }

  &__subscribe-note {
    margin-top: 8px;
    font-size: 12px;
    color: #444444;
    display: flex;
  }

  &__subscribe-note--submitted {
    background-color: #dcfce7;
    border-radius: 8px;
    padding: 8px;
    align-items: center;
  }

  &__subscribe-note-icon {
    display: flex;
    margin-right: 8px;
    vertical-align: middle;
    color: #00a65f;
    width: 20px;
    height: 20px;
  }

  @media (max-width: 767px) {
    &__subscribe-note-icon {
      width: 30px;
      height: 30px;
    }
  }

  &__subscribe-note-text {
    vertical-align: middle;
    color: black;
  }

  &__subscribe-error {
    margin-top: 4px;
    font-size: 12px;
    color: $red-error;
  }
}
</style>
